/*
 * Thaheb cart drawer helpers (draft).
 *
 * The upsell "Add" buttons and the Express service switch are plain markup rendered by cart-summary.liquid.
 * Inline scripts inside a morphed section never run, so the behaviour lives here: one delegated click handler
 * on the document, which keeps working however often the drawer is re-rendered.
 *
 * Writes go through /cart/update.js (not /cart/add.js) so the product form's property interceptor on the
 * storefront cannot attach a personalisation to an upsell. After each write a "cart:update" event with the
 * rendered sections is dispatched, which the theme's cart-items-component turns into an in-place re-render.
 */
(function () {
  'use strict';

  if (window.thahebCart) return;

  var ERROR_TEXT = "Couldn't update your bag, please try again";
  var JSON_HEADERS = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  };

  function route(name, fallback) {
    var routes = (window.Theme && window.Theme.routes) || {};
    return routes[name] || fallback;
  }

  function sectionIds() {
    var seen = {};
    var ids = [];
    document.querySelectorAll('cart-items-component').forEach(function (el) {
      var id = el.dataset && el.dataset.sectionId;
      if (id && !seen[id]) {
        seen[id] = true;
        ids.push(id);
      }
    });
    return ids;
  }

  // One cart write at a time, in the order the customer tapped.
  var queue = Promise.resolve();
  function serial(task) {
    var run = queue.then(task, task);
    queue = run.catch(function () {});
    return run;
  }

  function parse(text) {
    try {
      return JSON.parse(text);
    } catch (_) {
      return null;
    }
  }

  function failure(data, status) {
    var error = new Error((data && (data.description || data.message)) || 'Request failed');
    error.status = status;
    return error;
  }

  function postJSON(url, body) {
    return fetch(url, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) }).then(function (res) {
      return res.text().then(function (text) {
        var data = parse(text);
        if (!res.ok) throw failure(data, res.status);
        return data || {};
      });
    });
  }

  function getCart() {
    return fetch(route('cart_url', '/cart') + '.js', { headers: { Accept: 'application/json' } }).then(function (res) {
      if (!res.ok) throw failure(null, res.status);
      return res.json();
    });
  }

  // XMLHttpRequest on purpose: the product form's fetch interceptor would merge its properties into a fetch add.
  function addViaXhr(variantId, quantity, sections) {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open('POST', route('cart_add_url', '/cart/add.js'));
      Object.keys(JSON_HEADERS).forEach(function (name) {
        xhr.setRequestHeader(name, JSON_HEADERS[name]);
      });
      xhr.onload = function () {
        var data = parse(xhr.responseText);
        if (xhr.status < 200 || xhr.status >= 300) {
          reject(failure(data, xhr.status));
          return;
        }
        getCart().then(function (cart) {
          cart.sections = data && data.sections;
          resolve(cart);
        }, reject);
      };
      xhr.onerror = function () {
        reject(new Error('Network error'));
      };
      xhr.send(
        JSON.stringify({
          items: [{ id: Number(variantId), quantity: quantity }],
          sections: sections,
          sections_url: window.location.pathname
        })
      );
    });
  }

  function writeCart(variantId, quantity) {
    var sections = sectionIds().join(',');
    var updates = {};
    updates[variantId] = quantity;
    var body = { updates: updates };
    if (sections) {
      body.sections = sections;
      body.sections_url = window.location.pathname;
    }

    return postJSON(route('cart_update_url', '/cart/update.js'), body).then(function (cart) {
      var present = (cart.items || []).some(function (item) {
        return String(item.variant_id) === String(variantId);
      });
      if (quantity > 0 && !present) return addViaXhr(variantId, quantity, sections);
      return cart;
    });
  }

  function announce(cart) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'thaheb-cart',
          data: { source: 'thaheb-cart', itemCount: cart.item_count, sections: cart.sections }
        }
      })
    );
  }

  function refresh() {
    return getCart().then(function (cart) {
      delete cart.sections;
      announce(cart);
      return cart;
    });
  }

  var errorTimers = new WeakMap();

  function showUpsellError(from) {
    var scope = from.closest('[data-thaheb-upsell]') || document.querySelector('[data-thaheb-upsell]');
    var line = scope && scope.querySelector('.thaheb-upsell__error');
    if (!line) return;
    line.textContent = ERROR_TEXT;
    line.hidden = false;
    clearTimeout(errorTimers.get(line));
    errorTimers.set(
      line,
      setTimeout(function () {
        line.hidden = true;
        line.textContent = '';
      }, 5000)
    );
  }

  /*
   * Smooth line changes. The server needs a second or more to answer a quantity change or an Express switch, and
   * the row that results (or goes away) used to appear then, pushing the upsell row and footer down or up.
   * At tap time we now hold the space: a skeleton row grows where the new row will be, or the row that is about
   * to go collapses, both inside the half second after the tap. The real render swaps in at the same height.
   */
  var EXPRESS_ROW_H = 91;
  var EXPRESS_ROW_MB = 14;
  var held = [];
  var HOLD_MS = 15000;

  function tableRow(el) {
    return el && el.closest ? el.closest('.cart-items__table-row') : null;
  }

  function isLastRow(row) {
    var next = row.nextElementSibling;
    while (next && next.classList.contains('thaheb-reserve')) next = next.nextElementSibling;
    return !next;
  }

  function rememberExpressHeight() {
    var row = document.querySelector('.cart-drawer__dialog .thaheb-express-line');
    if (row && row.offsetHeight > 40) {
      EXPRESS_ROW_H = row.offsetHeight;
      EXPRESS_ROW_MB = parseFloat(getComputedStyle(row).marginBottom) || EXPRESS_ROW_MB;
    }
  }

  // Skeleton row that grows to `height` px right after `row`.
  function reserveAfter(row, height, margin, before) {
    if (!row || !row.parentElement || !(height > 0)) return null;
    var next = before ? row.previousElementSibling : row.nextElementSibling;
    if (next && next.classList.contains('thaheb-reserve')) return null;

    // Not a <tr>: the morph would reuse a skeleton row as the real row and report its old box as a layout shift.
    var tr = document.createElement('div');
    tr.className = 'thaheb-reserve';
    tr.setAttribute('aria-hidden', 'true');
    tr.setAttribute('role', 'presentation');
    tr.style.setProperty('--thaheb-reserve-h', Math.round(height) + 'px');
    tr.style.setProperty('--thaheb-reserve-mb', (margin || 0) + 'px');
    var media = document.createElement('span');
    media.className = 'thaheb-reserve__media';
    var lines = document.createElement('span');
    lines.className = 'thaheb-reserve__lines';
    lines.appendChild(document.createElement('span'));
    lines.appendChild(document.createElement('span'));
    tr.appendChild(media);
    tr.appendChild(lines);

    row.insertAdjacentElement(before ? 'beforebegin' : 'afterend', tr);
    held.push({ type: 'reserve', el: tr });
    setTimeout(function () {
      if (tr.isConnected) tr.remove();
    }, HOLD_MS);
    return tr;
  }

  // The tapped row's own space in the list (its height plus the spacing it has when it is not last).
  // Shopify splits the extra piece into its own line and the 50% offer adds text: the old line gains a one-line
  // note, the new line gets the note plus the sale and regular prices. Measured on phone and desktop.
  var SPLIT_NOTE_H = 26;
  var SPLIT_SALE_H = 48;

  function reserveForNewLine(row) {
    var cs = getComputedStyle(row);
    var last = isLastRow(row);
    var rect = row.getBoundingClientRect();
    // The old line gains its note: keep that room (plus the spacing it gets once it is no longer last).
    if (!/50% off/i.test(row.textContent || '')) {
      row.style.minHeight = Math.round(rect.height + SPLIT_NOTE_H + (last ? 15 : 0)) + 'px';
      held.push({ type: 'minh', el: row });
    }
    // The new line is the tapped one plus the offer text. If the tapped row is last, the new one will be last too.
    return reserveAfter(row, rect.height + SPLIT_SALE_H, last ? 0 : parseFloat(cs.marginBottom) || 0);
  }

  function trimRow(row, by) {
    var from = Math.round(row.getBoundingClientRect().height);
    row.style.overflow = 'hidden';
    if (!prefersReducedMotion()) {
      row.style.height = from + 'px';
      row.style.transition = 'height 0.2s ease';
      void row.offsetHeight;
    }
    row.style.height = from - by + 'px';
    held.push({ type: 'trim', el: row });
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function collapseRow(row) {
    if (!row || row.classList.contains('thaheb-collapsing')) return;
    var cs = getComputedStyle(row);
    var prev = row.previousElementSibling;
    var willBeLast = isLastRow(row) && prev && prev.classList.contains('cart-items__table-row') ? prev : null;
    row.style.setProperty('--thaheb-row-h', row.getBoundingClientRect().height + 'px');
    row.style.setProperty('--thaheb-row-mb', cs.marginBottom);
    row.classList.add('thaheb-collapsing');
    if (willBeLast) {
      willBeLast.classList.add('thaheb-now-last');
      // A split pair goes back to one line: the remaining line loses its offer note too.
      var pairText = /50% off/i.test(row.textContent || '') && /50% off/i.test(willBeLast.textContent || '');
      if (pairText) trimRow(willBeLast, SPLIT_NOTE_H + 15);
    }
    held.push({ type: 'collapse', el: row, prev: willBeLast });
    setTimeout(releaseHeld, HOLD_MS);
  }

  // The row is about to go (theme remove button): the row above loses its trailing space at the same moment.
  function prepareRemove(row) {
    // Only when other lines stay: removing the only line shows the empty cart, which the theme handles itself.
    if (!row || document.querySelectorAll('.cart-drawer__dialog tbody .cart-items__table-row').length < 2) return;
    collapseRow(row);
  }

  function releaseHeld() {
    var list = held;
    held = [];
    list.forEach(function (h) {
      if (!h.el || !h.el.isConnected) return;
      if (h.type === 'reserve') {
        h.el.remove();
      } else if (h.type === 'collapse') {
        h.el.classList.remove('thaheb-collapsing');
        h.el.style.removeProperty('--thaheb-row-h');
        h.el.style.removeProperty('--thaheb-row-mb');
        if (h.prev) h.prev.classList.remove('thaheb-now-last');
      } else if (h.type === 'minh') {
        h.el.style.removeProperty('min-height');
      } else if (h.type === 'trim') {
        ['height', 'overflow', 'transition'].forEach(function (k) {
          h.el.style.removeProperty(k);
        });
      } else if (h.type === 'prev') {
        h.el.classList.remove('thaheb-now-last');
      }
    });
  }

  var toastTimer = null;
  var toastUntil = 0;
  var toastWatch = null;
  // The drawer is re-rendered after a failed change, which would wipe the message: put it back until its time is up.
  function watchBagError() {
    clearInterval(toastWatch);
    toastWatch = setInterval(function () {
      if (Date.now() > toastUntil - 400) {
        clearInterval(toastWatch);
        return;
      }
      var dialog = document.querySelector('cart-drawer-component dialog[open]');
      var host = dialog || document.body;
      if (!host.querySelector(':scope > .thaheb-bag-error.is-visible')) bagError(true);
    }, 200);
  }

  function bagError(keepTimer) {
    var dialog = document.querySelector('cart-drawer-component dialog[open]');
    var host = dialog || document.body;
    var el = host.querySelector(':scope > .thaheb-bag-error');
    if (!el) {
      el = document.createElement('p');
      el.className = 'thaheb-bag-error' + (dialog ? '' : ' thaheb-bag-error--page');
      el.setAttribute('role', 'alert');
      el.addEventListener('click', function () {
        el.classList.remove('is-visible');
        toastUntil = 0;
      });
      host.appendChild(el);
    }
    el.textContent = ERROR_TEXT;
    void el.offsetWidth;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    if (!keepTimer) {
      toastUntil = Date.now() + 6000;
      watchBagError();
    }
    toastTimer = setTimeout(function () {
      el.classList.remove('is-visible');
    }, Math.max(0, toastUntil - Date.now()));
  }

  // A quantity change, remove or add failed: put every optimistic change back and show the message.
  function updateFailed() {
    releaseHeld();
    document.querySelectorAll('input[data-cart-quantity]').forEach(function (input) {
      var real = input.getAttribute('data-cart-quantity');
      if (real !== null && real !== '' && input.value !== real) input.value = real;
    });
    bagError();
    refresh().catch(function () {});
  }

  // The server can take a few seconds to render the drawer, so the tapped card turns into a row at once.
  // The morph that follows the write replaces it with the real line; a failure puts the card back.
  function showPendingRow(button) {
    var card = button.closest('[data-thaheb-upsell-card]');
    var drawer = button.closest('cart-drawer-component');
    if (!card || !drawer) return null;

    var form = drawer.querySelector('form.cart-form');
    var list = drawer.querySelector('.cart-drawer__items');
    if (!form && !list) return null;

    var row = document.createElement('div');
    row.className = 'thaheb-adding thaheb-adding--upsell';
    row.setAttribute('data-thaheb-optimistic-row', '');
    row.setAttribute('role', 'status');

    var media = document.createElement('div');
    media.className = 'thaheb-adding__media';
    var source = card.querySelector('img');
    var src = source && (source.currentSrc || source.src);
    if (src) {
      var img = document.createElement('img');
      img.alt = '';
      img.src = src;
      media.appendChild(img);
    }

    var body = document.createElement('div');
    body.className = 'thaheb-adding__body';
    var title = document.createElement('p');
    title.className = 'thaheb-adding__title';
    var heading = card.querySelector('.thaheb-upsell__title');
    title.textContent = heading ? heading.textContent.trim() : '';
    var note = document.createElement('p');
    note.className = 'thaheb-adding__note';
    note.textContent = 'Adding to your cart';
    body.appendChild(title);
    body.appendChild(note);
    row.appendChild(media);
    row.appendChild(body);

    if (form) form.insertAdjacentElement('beforebegin', row);
    else list.insertAdjacentElement('afterbegin', row);
    card.style.display = 'none';

    return {
      restore: function () {
        row.remove();
        card.style.display = '';
      },
      settle: function () {
        setTimeout(function () {
          if (row.isConnected) row.remove();
        }, 10000);
      }
    };
  }

  function onUpsellAdd(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    var variantId = button.dataset.variantId;
    if (!variantId) return;

    button.setAttribute('aria-busy', 'true');
    var pending = showPendingRow(button);
    serial(function () {
      return writeCart(variantId, 1);
    })
      .then(function (cart) {
        announce(cart);
        if (pending) pending.settle();
      })
      .catch(function () {
        if (pending) pending.restore();
        showUpsellError(button);
      })
      .then(function () {
        if (button.isConnected) button.removeAttribute('aria-busy');
      });
  }

  function onExpressToggle(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    var variantId = button.dataset.variantId;
    if (!variantId) return;

    var wasOn = button.getAttribute('aria-checked') === 'true';
    button.setAttribute('aria-checked', String(!wasOn));
    button.setAttribute('aria-busy', 'true');

    // Hold the space for the Express line (or collapse it) at tap time so nothing below it jumps later.
    rememberExpressHeight();
    var expressRow = document.querySelector('.cart-drawer__dialog .thaheb-express-line');
    if (wasOn) {
      collapseRow(expressRow);
    } else if (!expressRow) {
      // New lines are listed first, so the Express line lands above the others.
      var firstRow = document.querySelector('.cart-drawer__dialog tbody .cart-items__table-row');
      if (firstRow) reserveAfter(firstRow, EXPRESS_ROW_H, EXPRESS_ROW_MB, true);
    }

    serial(function () {
      return writeCart(variantId, wasOn ? 0 : 1);
    })
      .then(announce)
      .catch(function () {
        releaseHeld();
        if (button.isConnected) {
          button.setAttribute('aria-checked', String(wasOn));
        }
        bagError();
        refresh().catch(function () {});
      })
      .then(function () {
        if (button.isConnected) button.removeAttribute('aria-busy');
      });
  }

  // Capture phase: runs before the theme's own handlers start the quantity change.
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target instanceof Element ? event.target : null;
      if (!target || !target.closest('.cart-drawer__dialog')) return;

      var plus = target.closest('button.quantity-plus');
      if (plus && !plus.disabled) {
        var row = tableRow(plus);
        // Shopify splits the extra piece into its own line (50% offer), so a line of the same size is coming.
        if (row && !row.classList.contains('thaheb-express-line')) reserveForNewLine(row);
        return;
      }

      var minus = target.closest('button.quantity-minus');
      if (minus && !minus.disabled) {
        var minusRow = tableRow(minus);
        var input = minusRow && minusRow.querySelector('input[type="number"], input[data-cart-quantity]');
        // Going from 1 to 0 removes the line.
        if (input && parseInt(input.value, 10) <= 1) prepareRemove(minusRow);
        return;
      }

      var remove = target.closest('.cart-items__remove');
      if (remove) prepareRemove(tableRow(remove));
    },
    true
  );

  document.addEventListener('click', function (event) {
    var target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    var add = target.closest('[data-thaheb-upsell-add]');
    if (add) {
      event.preventDefault();
      onUpsellAdd(add);
      return;
    }

    var express = target.closest('[data-thaheb-express-toggle]');
    if (express) {
      event.preventDefault();
      onExpressToggle(express);
    }
  });

  window.thahebCart = { refresh: refresh, bagError: bagError, updateFailed: updateFailed, version: 3 };
})();
